/**
 * The Vault workspace's hookup to Fias AI — what the assistant can DO on the
 * screen, and what it can SEE of it.
 *
 * Two planes, never mixed (docs/arche-ai-interface.md):
 *   - SERVER tools find and change files (documents_list, documents_trash, …)
 *     through the entity gateway, with their own risk tiers and cards.
 *   - These UI ACTIONS only move the view — open a folder, open a file the
 *     preferred way, show where things are, switch view, change how a folder
 *     is viewed. Nothing here changes a file or a folder (the UI plane is pure
 *     client state), and an action can't return data: the assistant learns
 *     ids from the server tools first.
 *
 * The state snapshot is IDS ONLY. It goes into the assistant's context as
 * trusted text, and file and folder names can be written by anyone (an email
 * attachment's name is its sender's choice) — so no names, ever. The server
 * validates and caps every id again before echoing it
 * (`buildVaultDocumentsActiveContextSection`).
 */
import { useRegisterArcheAI } from '@/components/fias-ai/hooks/useRegisterArcheAI';
import type { ArcheAIAction } from '@/components/fias-ai/types';
import { ARCHE_ID } from '../constants';
import { VIEW_MODES, SORT_KEYS, type FolderView } from '../lib/desktop-layout';
import type { DesktopStyle } from '../lib/folder-view';
import type { WorkspaceView } from '../lib/workspace-view';

const FOLDER_ID = '^(root|vdf_[a-f0-9]{32})$';
const DOCUMENT_ID = '^vdoc_[a-f0-9]{32}$';

export const WORKSPACE_AI_ACTIONS: ArcheAIAction[] = [
  {
    id: 'open_folder',
    description:
      'Show a folder on screen: on the Desktop it opens (or brings forward) a window on it; in Browse it goes to it. folderId is a vdf_ id from documents_list / the context, or "root" for the top of Documents. newWindow opens another window even if one already shows it.',
    parameterSchema: {
      type: 'object',
      additionalProperties: false,
      required: ['folderId'],
      properties: {
        folderId: { type: 'string', pattern: FOLDER_ID, maxLength: 40 },
        newWindow: { type: 'boolean' },
      },
    },
  },
  {
    id: 'open_file',
    description:
      'Open a file the preferred way: an image or PDF in a viewer (with “Open in …” for an arche that can edit it), a Word document in its arche when one is available, otherwise a preview. documentId is a vdoc_ id.',
    parameterSchema: {
      type: 'object',
      additionalProperties: false,
      required: ['documentId'],
      properties: { documentId: { type: 'string', pattern: DOCUMENT_ID, maxLength: 40 } },
    },
  },
  {
    id: 'reveal_items',
    description:
      'Show where files or folders are and select them there (the folder they are in is opened). Use after finding them with documents_list. Up to 50 ids.',
    parameterSchema: {
      type: 'object',
      additionalProperties: false,
      properties: {
        documentIds: {
          type: 'array',
          maxItems: 50,
          items: { type: 'string', pattern: DOCUMENT_ID, maxLength: 40 },
        },
        folderIds: {
          type: 'array',
          maxItems: 50,
          items: { type: 'string', pattern: '^vdf_[a-f0-9]{32}$', maxLength: 40 },
        },
      },
    },
  },
  {
    id: 'switch_view',
    description:
      'Switch the workspace between Desktop (icons and windows), Browse (folder tree and list) and Search (filters).',
    parameterSchema: {
      type: 'object',
      additionalProperties: false,
      required: ['view'],
      properties: { view: { type: 'string', enum: ['desktop', 'browse', 'search'] } },
    },
  },
  {
    id: 'set_folder_view',
    description:
      'Change how a folder is shown in its Desktop window: mode (icons-xl, icons-lg, icons-md, icons-sm, list, details, columns, gallery) and/or sort (name, modified, type, size) and direction (asc, desc). Remembered for that folder.',
    parameterSchema: {
      type: 'object',
      additionalProperties: false,
      required: ['folderId'],
      properties: {
        folderId: { type: 'string', pattern: FOLDER_ID, maxLength: 40 },
        mode: { type: 'string', enum: [...VIEW_MODES] },
        sort: { type: 'string', enum: [...SORT_KEYS] },
        direction: { type: 'string', enum: ['asc', 'desc'] },
      },
    },
  },
];

export interface WorkspaceAIState {
  view: WorkspaceView;
  holderId: string;
  /** The focused window's container, or Browse's folder; `root`, a vdf_ id. */
  openFolderId: string | null;
  openFolderIds: string[];
  selectedDocumentIds: string[];
  selectedFolderIds: string[];
  viewingDocumentId: string | null;
  desktopStyle: DesktopStyle;
}

export interface WorkspaceAIDeps {
  /** Off for workspaces the assistant can't act in (guild spaces). */
  enabled: boolean;
  getState: () => WorkspaceAIState;
  openFolder: (folderId: string, newWindow: boolean) => void | Promise<void>;
  openFile: (documentId: string) => void | Promise<void>;
  reveal: (ids: { documentIds: string[]; folderIds: string[] }) => void | Promise<void>;
  switchView: (view: WorkspaceView) => void;
  setFolderView: (folderId: string, patch: Partial<FolderView>) => void | Promise<void>;
  /** The assistant changed files — refresh what's on screen. */
  onDataChanged?: () => void;
}

const ids = (value: unknown, pattern: RegExp, max: number): string[] =>
  Array.isArray(value)
    ? value.filter((v): v is string => typeof v === 'string' && pattern.test(v)).slice(0, max)
    : [];

/** Apply one UI action — validated here too (the model's params are input). */
export async function applyWorkspaceAction(
  deps: WorkspaceAIDeps,
  actionId: string,
  params: Record<string, unknown>,
): Promise<void> {
  const folderRe = new RegExp(FOLDER_ID);
  const docRe = new RegExp(DOCUMENT_ID);
  switch (actionId) {
    case 'open_folder': {
      const folderId = params.folderId;
      if (typeof folderId !== 'string' || !folderRe.test(folderId)) throw new Error('Bad folderId');
      await deps.openFolder(folderId, params.newWindow === true);
      return;
    }
    case 'open_file': {
      const documentId = params.documentId;
      if (typeof documentId !== 'string' || !docRe.test(documentId)) {
        throw new Error('Bad documentId');
      }
      await deps.openFile(documentId);
      return;
    }
    case 'reveal_items': {
      const documentIds = ids(params.documentIds, docRe, 50);
      const folderIds = ids(params.folderIds, /^vdf_[a-f0-9]{32}$/, 50);
      if (documentIds.length + folderIds.length === 0) throw new Error('Nothing to reveal');
      await deps.reveal({ documentIds, folderIds });
      return;
    }
    case 'switch_view': {
      const view = params.view;
      if (view !== 'desktop' && view !== 'browse' && view !== 'search') throw new Error('Bad view');
      deps.switchView(view);
      return;
    }
    case 'set_folder_view': {
      const folderId = params.folderId;
      if (typeof folderId !== 'string' || !folderRe.test(folderId)) throw new Error('Bad folderId');
      const patch: Partial<FolderView> = {};
      if ((VIEW_MODES as readonly unknown[]).includes(params.mode)) {
        patch.mode = params.mode as FolderView['mode'];
      }
      if ((SORT_KEYS as readonly unknown[]).includes(params.sort)) {
        patch.sort = params.sort as FolderView['sort'];
      }
      if (params.direction === 'asc' || params.direction === 'desc') patch.dir = params.direction;
      await deps.setFolderView(folderId, patch);
      return;
    }
    default:
      throw new Error(`Unknown action ${actionId}`);
  }
}

export function useWorkspaceAI(deps: WorkspaceAIDeps): void {
  useRegisterArcheAI({
    archeId: ARCHE_ID,
    enabled: deps.enabled,
    archeDescription:
      'The Vault workspace (My Data): the person’s files and folders across Documents, Arche Saves and My-Fias, shown as a Desktop (icons and windows), Browse (folder tree) or Search.',
    actions: WORKSPACE_AI_ACTIONS,
    getStateSnapshot: () => {
      const state = deps.getState();
      return { ...state };
    },
    applyAction: (actionId, params) => applyWorkspaceAction(deps, actionId, params),
    onExternalDataChange: (domains) => {
      if (domains.includes(ARCHE_ID)) deps.onDataChanged?.();
    },
  });
}
